import {IReg, REG} from "lib/commons/registry";
import {BaseElementAsync, OSkinableInit} from "back/commons/basis";
import {DOM, JSX} from "lib/commons/xml/dom";
import {AREAS, IArea, IControlAreaContext} from "lib/commons/areas";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {FORMS} from "lib/commons/forms";
import {IView} from "lib/commons/views";
import {IBasicUniversePointer} from "lib/core/universe";
import {ERROR} from "lib/core/errorReport";
import {IUserAreaContext} from "back/core/dialogs/userAreas";
import {EUserAspects, JUser} from "lib/core/user";
import {UserSelfSrv} from "lib/core/userSelf";
import {DOMSH} from "lib/commons/xml/domsh";

/**
 * Écran de modifification du profile d'un utilisateur
 */
export interface UserProfile extends BaseElementAsync {
	initialize(init: OUserProfileInit): this
}

export interface OUserProfileInit extends OSkinableInit {
	reg: IReg<IBasicUniversePointer>
}

export class UserProfile extends BaseElementAsync implements IView, IControlAreaContext, IUserAreaContext {
	reg: IReg<IBasicUniversePointer>;

	// IControlAreaContext
	buildControlLabel = true;
	buildUnlockBtn = true;

	// IUserAreaContext
	uiContextSgn: string;
	userSelfSrv: UserSelfSrv
	users?: JUser[] = null;

	protected _form: HTMLFormElement;
	protected _emptyData: any;
	protected _userData: string;
	protected _boundUpdateForm: any;
	protected _revertBtn: Button;
	protected _saveBtn: Button;

	protected async _initialize(init: OUserProfileInit) {
		this.reg = init.reg;

		this.userSelfSrv = this.reg.env.universe.userSelf;

		this.uiContextSgn = "@user#self#edit";

		this._revertBtn = new Button().initialize({uiContext: "dialog", label: "Rétablir", disabled: true});
		this._saveBtn = new Button().initialize({uiContext: "dialog", label: "Enregistrer", disabled: true});
		const closeBtn = new Button().initialize({uiContext: "dialog", label: "Fermer"});

		this._form = <form id="form">
			<fieldset id="fieldset">
				<div id="common">
					<div class="fields" area-ids="account nickNames authMethod"/>
					<div class="fields" area-ids="firstName lastName email"/>
				</div>
				<div class="fields" area-ids="*"/>
			</fieldset>
		</form> as HTMLFormElement;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("standard-dialog", sr);
		this.reg.installSkin("form-control-areas", sr);
		this._initAndInstallSkin(this.localName, init);
		DOM.append(sr,
			this._form,
			<div id="footer">
				{this._revertBtn}
				{this._saveBtn}
				{closeBtn}
			</div>
		);

		await AREAS.applyLayout(this._form, await Promise.all(this.reg.getList<IArea | Promise<IArea>>('userProps:profile')), this);

		this._emptyData = FORMS.formToJson(this._form);
		await this._updateForm(false);
		this._form.checkValidity();
		this._boundUpdateForm = await this._updateForm.bind(this) as any;
		this.reg.env.universe.auth.listeners.on('loggedUserChanged', this._boundUpdateForm);

		const checkDirtyState = () => {
			const user = FORMS.formToJson(this._form);
			const dirty = JSON.stringify(user) !== this._userData;
			this._revertBtn.disabled = this._saveBtn.disabled = !dirty;
		};

		let checkDirtyRequest = 0;
		this._form.addEventListener('input', () => {
			if (checkDirtyRequest) {
				clearTimeout(checkDirtyRequest);
				checkDirtyRequest = 0;
			}
			checkDirtyRequest = setTimeout(checkDirtyState, 300) as any;
		});

		closeBtn.onclick = () => {
			POPUP.findPopupableParent(this).close();
		};

		this._revertBtn.onclick = this._boundUpdateForm;

		this._saveBtn.onclick = async () => {
			if (this._form.reportValidity()) {
				const user = FORMS.formToJson(this._form);
				delete user.authMethod;
				delete user.account;
				try {
					const resp = await this.reg.env.universe.auth.updateCurrentUser(user);
					if (resp) {
						await this._updateForm(true);
					} else
						throw "User not created";
				} catch (e) {
					await ERROR.report(`Une erreur est survenue lors de la mise à jour du profil. Veuillez réessayer ultérieurement.`, e);
				}
			}
		}
	}

	protected async _updateForm(clear: boolean = true) {
		const {auth} = this.reg.env.universe;
		const user = auth.currentAuthenticatedUser;
		this.users = [auth.currentAuthenticatedUser];
		if (clear) FORMS.jsonToForm(this._emptyData, this._form);
		FORMS.jsonToForm(user, this._form, true);
		this._userData = JSON.stringify(FORMS.formToJson(this._form));
		// Désactivation de tout le formulaire si l'utilisateur est readOnly
		const fieldset = this._form.elements.namedItem('fieldset') as HTMLFieldSetElement;
		let readonly = user.isReadOnly || user.isDisabled || !this.reg.env.universe.userSelf.hasAspect(EUserAspects.updatable);
		fieldset.disabled = readonly;
		DOM.setHidden(this._revertBtn, readonly);
		DOM.setHidden(this._saveBtn, readonly);
		if (clear) this._revertBtn.disabled = this._saveBtn.disabled = true;
	}

	onViewHidden(closed?: boolean): void {
		const {auth} = this.reg.env.universe;
		if (this._boundUpdateForm) auth.listeners.removeListener('loggedUserChanged', this._boundUpdateForm);
	}
}

REG.reg.registerSkin('c-user-profile', 1, /* language=CSS */ `
	:host {
		min-width: 40em;
		min-height: 12em;
	}

	form {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		flex: 1;
		padding: 1em;
	}

	fieldset {
	  border: none;
	  padding: 0;
	  margin: 0;
  }

  #common {
	  display: flex;
	  flex: 1;
	  min-height: 0;
	  min-width: 0;
	  flex-wrap: wrap;
  }

  #common > .fields {
	  flex: 1;
	  display: flex;
	  flex-direction: column;
	  min-width: 10em;
  }

  #common > .fields:first-child {
	  margin-inline-end: 2em;
  }

  .fields div[hidden] {
	  display: none !important;
  }

  div[area-id=authMethod] {
		flex-direction: column;
	}

	div[area-id=authMethod] > div {
		display: flex;
		min-height: 0;
		min-width: 0;
	}
`);

customElements.define('c-user-profile', UserProfile);

REG.reg.addSvcToList("userProps:profile", 'account', 1, 'area.users.props');
REG.reg.addSvcToList("userProps:profile", 'firstName', 1, 'area.users.firstName');
REG.reg.addSvcToList("userProps:profile", 'lastName', 1, 'area.users.lastName');
REG.reg.addSvcToList("userProps:profile", 'nickNames.first', 1, 'area.users.nickNames.first');
REG.reg.addSvcToList("userProps:profile", 'email', 1, 'area.users.email.user');

